import { useState, useEffect } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  className?: string;
}

/**
 * Component that fetches images with authentication headers.
 * Regular <img> tags don't send Authorization headers, causing 401 errors
 * for protected endpoints. This component fetches the image with proper
 * auth headers and displays it using a blob URL.
 */
export function AuthenticatedImage({ src, alt, className }: AuthenticatedImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function fetchImage() {
      setLoading(true);
      setError(null);

      try {
        const token = await useAuthStore.getState().getAccessToken();
        const response = await fetch(src, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }

        const blob = await response.blob();

        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load image');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  // Clean up blob URL when component unmounts or src changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center bg-gray-100 ${className}`}>
        <ImageOff className="h-12 w-12 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <img
      src={blobUrl || ''}
      alt={alt}
      className={className}
    />
  );
}
